/**
 * @fileoverview Resolve one MFA string: consumer `t` → English default.
 * @module @puffin-core/mfa-ui/strings/resolveString
 *
 * A translator's return is treated as MISSING — and the English default used — when it is nullish,
 * empty, or THE KEY ECHOED BACK, because the last of those is exactly what react-i18next returns for
 * an unregistered key. Without that branch an unwired app renders the raw key as visible text on the
 * MFA challenge.
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20): bound from the ONE shared implementation. Seven byte-similar
 * copies of this helper existed, differing only in the imported defaults constant — a rule-of-three
 * violation that ran to seven.
 *
 * 🔴 The invariant is now proved by BEHAVIOUR, not by source text.
 * `tests/cross-app/contracts/loginStringsWiring.contract.test.js` CALLS this function through five
 * arms (echoed key · empty · nullish · no translator · usable) plus an arity probe. It used to grep
 * this file for the literal `/v !== key/`, which a binding like this one would have failed — and,
 * more to the point, which was green for `page-shell` and `onboarding-ui` while their helpers could
 * not resolve a single real key.
 *
 * The import is the React-free `/strings-kit` subpath: this file is part of the leaf-only `strings/`
 * subtree that `@puffin-core/i18n-namespaces` loads at app boot.
 */

import { createStringResolver } from '@puffin-core/i18n-bootstrap/strings-kit';

import { DEFAULT_MFA_STRINGS } from './en.js';

/**
 * @param {((key: string, params?: Object) => string|undefined|null)|undefined|null} t - Optional
 *   consumer translator. `params` is forwarded only when supplied, so a consumer `t` whose arity is
 *   asserted by its own tests still sees a single argument.
 * @param {string} key - The string key.
 * @param {Object} [params] - Optional interpolation params forwarded to the consumer's `t`.
 * @returns {string|undefined} The resolved string, or `undefined` for an unknown key.
 */
export const resolveString = createStringResolver(DEFAULT_MFA_STRINGS);

export default resolveString;
